import React, { useCallback, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import RichTextField from 'components/inputs/RichTextField';
import { LanguageSelector } from './style';

function toLangMap(value, lang = 'de') {
  if (!value) return { de: '', en: '' };
  if (typeof value === 'object' && !Array.isArray(value)) {
    return { de: value.de || '', en: value.en || '' };
  }
  if (Array.isArray(value)) {
    return value.reduce(
      (acc, cur) => {
        if (cur?.lang) acc[cur.lang] = cur.value || '';
        return acc;
      },
      { de: '', en: '' }
    );
  }
  if (typeof value === 'string') {
    return { de: '', en: '', [lang]: value };
  }
  return { de: '', en: '' };
}

function MultiLanguageRichTextField({
  id,
  language = 'de',
  value = null,
  onChange,
  onAddSync,
  ...rest
}) {
  const [lang, setLang] = useState(language || 'de');
  // Live HTML per language — useForm freezes the displayed value while typing.
  const cacheRef = useRef({ de: '', en: '' });
  const loadedRef = useRef({ de: false, en: false });

  // Seed each language once from the form (page load). Cache owns it after that.
  const formMap = toLangMap(value, lang);
  ['de', 'en'].forEach(code => {
    if (!loadedRef.current[code] && formMap[code]) {
      cacheRef.current[code] = formMap[code];
      loadedRef.current[code] = true;
    }
  });

  useEffect(() => {
    if (!value && onAddSync) {
      onAddSync(id, { de: '', en: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mergeCache = useCallback(
    extra => {
      const base =
        value && typeof value === 'object' && !Array.isArray(value) ? value : {};
      return {
        ...base,
        de: cacheRef.current.de,
        en: cacheRef.current.en,
        ...extra
      };
    },
    [value]
  );

  const handleChange = useCallback(
    (newValue, tinymcePath = '') => {
      cacheRef.current[lang] = newValue;
      loadedRef.current[lang] = true;
      onChange(mergeCache(), tinymcePath);
    },
    [lang, mergeCache, onChange]
  );

  const handleLanguageChange = useCallback(
    newLang => {
      const safeNewLang = newLang || 'de';
      if (safeNewLang === lang) return;

      // langswitch unfreezes useForm; then flush live cache so nothing is truncated.
      onChange({}, 'langswitch');
      onChange(mergeCache({ multiLangFieldActiveLang: safeNewLang }));
      setLang(safeNewLang);
    },
    [lang, mergeCache, onChange]
  );

  return (
    <RichTextField
      headerComponent={
        <LanguageSelector selectedLanguage={lang} onChange={handleLanguageChange} />
      }
      {...rest}
      lang={lang}
      value={cacheRef.current[lang] || ''}
      onChange={handleChange}
    />
  );
}

MultiLanguageRichTextField.propTypes = {
  id: PropTypes.string,
  language: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.array,
    PropTypes.oneOf([null])
  ]),
  onChange: PropTypes.func.isRequired,
  onAddSync: PropTypes.func
};

export default React.memo(MultiLanguageRichTextField);
